import {
  IonContent,
  IonIcon,
  IonLabel,
  IonList,
  IonListHeader,
  IonSpinner,
} from '@ionic/react';
import { ImageList, ImageListItem } from '@mui/material';
import { addOutline } from 'ionicons/icons';
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { Permissions } from '../../api/authorization.provider';
import {
  Attachment,
  AttachmentSourceType,
  useDeleteAttachmentMutation,
  useListAttachmentsQuery,
} from '../../api/graphql/generated';
import {
  ImageResolution,
  OnFileUploadResult,
  useUploadAttachmentLazy,
} from '../../api/hooks/attachment.hooks';
import { useListQueryWithInfiniteScroll } from '../../api/hooks/infinite-scroll.hooks';
import { trackLaunchedAttachmentPreview } from '../../config/analytics';
import i18n from '../../i18n';
import { tryFn } from '../../util/function';
import { ImagePreview } from '../image/image-preview';
import { SaveStatusToast } from '../toast/toast.gql';
import {
  AttachmentUploader,
  AttachmentUploaderRef,
} from './attachment-uploader';
import './attachment.css';

interface AttachmentActionsProps {
  onSelect: (attachmentId: string) => void;
  dismiss?: () => void;
  source: AttachmentSourceType;
  sourceId: string;
  // Because attachments have different sources and different permission rules
  // we require a function to return permissions for a given attachment
  getAttachmentPermission: (
    attachment: Attachment,
    permission: keyof Permissions
  ) => boolean;
}

const AttachmentActions = (props: AttachmentActionsProps): ReactElement => {
  const { dismiss, onSelect, source, sourceId, getAttachmentPermission } =
    props;

  const canUpload = getAttachmentPermission({} as Attachment, 'create');

  useEffect(() => {
    trackLaunchedAttachmentPreview();
  }, []);

  const { infiniteScroll, result: listAttachmentsResult } =
    useListQueryWithInfiniteScroll({
      useQuery: useListAttachmentsQuery,
      input: {
        source: source as AttachmentSourceType,
        sourceId,
      },
      queryOptions: {
        fetchPolicy: 'cache-and-network',
      },
    });

  const { loading, data: listAttachmentData, refetch } = listAttachmentsResult;

  const uploadAttachment = useUploadAttachmentLazy();
  const uploaderRef = useRef<AttachmentUploaderRef>(null);
  const [deleteAttachment] = useDeleteAttachmentMutation();

  // this uses the attachment uploader to open the file, may need to rename since it handles input and selection...doesn't do the uploading
  // just offloads the file change work to the parent
  const openFile = useCallback(() => {
    if (uploaderRef.current) {
      uploaderRef.current.openFile();
    }
  }, []);

  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | undefined>(undefined);

  const beforeFileUpload = () => {
    setIsUploading(true);
  };

  const onFileUpload = useCallback(
    (result: OnFileUploadResult) => {
      if (result?.fileKey === undefined) {
        setUploadError('The attachment failed to upload');
      }
      setIsUploading(false);
      refetch();
    },
    [refetch]
  );

  // this is the function that will do the uploading
  const onFileChange = useCallback(
    async (file: File) => {
      await uploadAttachment(
        [{ source, sourceId }],
        file,
        beforeFileUpload,
        onFileUpload
      );
    },
    [onFileUpload, source, sourceId, uploadAttachment]
  );

  const [removeAttachmentStatus, setRemoveAttachmentStatus] = useState<
    'loading' | 'success' | 'error' | undefined
  >(undefined);

  const onRemove = useCallback(
    async (id: string) => {
      setRemoveAttachmentStatus('loading');
      const { success, data: result } = await tryFn(
        () =>
          deleteAttachment({
            variables: {
              input: {
                id,
              },
            },
          }),
        {
          context: {
            operationName: 'deleteAttachment',
            entityType: 'attachment',
            entityId: id,
          },
        }
      );

      // if the operation failed or the attachment was not deleted, set the error status
      if (!success || !result?.data?.deleteAttachment) {
        setRemoveAttachmentStatus('error');
        return;
      }

      await refetch();
      setRemoveAttachmentStatus('success');
    },
    [deleteAttachment, refetch]
  );

  const attachments = listAttachmentData?.listAttachments.items ?? [];
  const hasAttachments = attachments.length > 0;

  const isLoading = loading && !hasAttachments;

  return (
    <IonContent scrollY={false}>
      <div className="attachment-actions-container ion-content-scroll-host">
        <IonList>
          <IonListHeader>{i18n.t('common:attachments')}</IonListHeader>
          {isLoading ? (
            <div className="attachment-loading-container">
              <IonSpinner />
            </div>
          ) : (
            <ImageList
              className="image-list-preview"
              gap={4}
              cols={3}
              rowHeight={95}
              sx={{ width: '100%' }}
            >
              {canUpload && (
                <ImageListItem
                  className="attachment-actions-button-container image-item-preview"
                  onClick={(e) => {
                    // stop progagation, but don't prevent default as it will prevent
                    // the file uploader from displaying
                    e.stopPropagation();

                    // TODO: this handler is getting called twice when clicked
                    openFile();
                  }}
                >
                  <AttachmentUploader
                    onFileChange={onFileChange}
                    ref={uploaderRef}
                    inputProps={{ accept: '.pdf,.png,.jpeg,.jpg' }}
                  />
                  <IonLabel color="primary">
                    <IonIcon icon={addOutline} size="large" />
                  </IonLabel>
                </ImageListItem>
              )}
              {isUploading && (
                <ImageListItem className="attachment-actions-image-list-item image-item-preview">
                  <IonSpinner className="attachment-preview-spinner" />
                </ImageListItem>
              )}
              {attachments.map((attachment) => {
                const canDelete = getAttachmentPermission(attachment, 'delete');

                return (
                  <ImagePreview
                    key={attachment.id}
                    attachment={attachment}
                    onSelect={() => {
                      onSelect(attachment.id);
                      if (dismiss) {
                        dismiss();
                      }
                    }}
                    resolution={ImageResolution.THUBMNAIL}
                    onRemove={() => onRemove(attachment.id)}
                    allowRemove={canDelete}
                  />
                );
              })}
            </ImageList>
          )}
        </IonList>
        <SaveStatusToast
          loading={removeAttachmentStatus === 'loading'}
          error={removeAttachmentStatus === 'error'}
          defaultErrorMessageKey={'common:attachment_removal_failed'}
          defaultMessageKey="common:attachment_removal_success"
          onDidDismiss={() => {
            setRemoveAttachmentStatus(undefined);
          }}
        />
        {infiniteScroll}
        <SaveStatusToast loading={isUploading} error={uploadError} />
      </div>
    </IonContent>
  );
};

export { AttachmentActions };
